// Intake review queue — Editor/Admin only.
// Lists pending submissions and lets a reviewer approve (promote to fact_deals)
// or reject (with a reason). Backend: /api/intake, /api/intake/:id/approve, /api/intake/:id/reject.

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { listIntake, approveIntake, rejectIntake } from '../lib/api'
import { useDims, useDeals } from '../lib/hooks'

// Same LINK_FIELDS definition as DealSidebar — keys match v_deals column names.
const LINK_FIELDS = [
  { key: 'link_main_folder',    label: 'Main folder' },
  { key: 'link_om',             label: 'OM' },
  { key: 'link_uw_model',       label: 'UW model' },
  { key: 'link_due_diligence',  label: 'Due diligence' },
  { key: 'link_financials',     label: 'Financials' },
  { key: 'link_reit_reports',   label: 'REIT reports' },
  { key: 'link_sources_uses',   label: 'Sources / uses' },
  { key: 'link_third_party_uw', label: '3rd party UW' },
]

const GOLD  = '#D4AF37'
const NAVY  = '#0A192F'
const NAVY2 = '#061020'
const MUTED = '#94A3B8'
const RED   = '#E74C3C'
const GREEN = '#22C55E'

const badgeStyle = { padding: '2px 8px', borderRadius: 3, fontSize: 10, letterSpacing: '0.08em', fontWeight: 600 }
const cardStyle  = {
  background: NAVY2, border: '1px solid rgba(212,175,55,0.15)', borderRadius: 8,
  padding: 18, display: 'flex', flexDirection: 'column', gap: 12,
}
const btnPrimary = {
  padding: '8px 16px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
  background: GOLD, color: NAVY, border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600,
}
const btnGhost = {
  padding: '8px 16px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
  background: 'transparent', color: MUTED, border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 4, cursor: 'pointer',
}


function fmtVal(f) {
  if (f == null) return '—'
  if (typeof f === 'number') return f.toLocaleString()
  return String(f)
}


function LinkUpdateCard({ item, deals, onApprove, onReject, busy }) {
  const [rejectMode, setRejectMode] = useState(false)
  const [reason, setReason] = useState('')

  const f = item.extracted_fields || {}
  const targetDeal = deals.find(d => d.id === f.deal_id)
  const targetName = targetDeal?.name || '(unknown deal)'
  const proposed = f.links || {}
  const diffs = LINK_FIELDS
    .map(({ key, label }) => ({
      key, label,
      before: targetDeal?.[key] || null,
      after: proposed[key] === undefined ? undefined : (proposed[key] || null),
    }))
    .filter(d => d.after !== undefined)

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <div style={{ color: '#FFFFFF', fontSize: 15, fontWeight: 600 }}>Link update: {targetName}</div>
        <div style={{ ...badgeStyle, background: 'rgba(59,130,246,0.15)', color: '#3B82F6' }}>LINK UPDATE</div>
        <div style={{ color: MUTED, fontSize: 11 }}>
          {f.deal_id} · from {item.sender_email || '?'} · {new Date(item.received_at).toLocaleString()}
        </div>
      </div>

      {/* Diff table */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
        {diffs.map(({ key, label, before, after }) => (
          <div key={key} style={{
            display: 'grid', gridTemplateColumns: '140px 1fr 1fr', gap: 10,
            padding: '8px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: 4,
          }}>
            <div style={{ color: MUTED, fontSize: 11 }}>{label}</div>
            <div>
              <div style={{ color: MUTED, fontSize: 10, textTransform: 'uppercase' }}>Before</div>
              <div style={{ color: '#94A3B8', fontSize: 11, wordBreak: 'break-all' }}>{before || '(empty)'}</div>
            </div>
            <div>
              <div style={{ color: GOLD, fontSize: 10, textTransform: 'uppercase' }}>After</div>
              <div style={{ color: '#FFFFFF', fontSize: 11, wordBreak: 'break-all' }}>{after || '(clear)'}</div>
            </div>
          </div>
        ))}
      </div>

      {f.notes && (
        <div style={{ color: MUTED, fontSize: 11, background: 'rgba(255,255,255,0.03)',
                      padding: '8px 12px', borderRadius: 4 }}>{f.notes}</div>
      )}

      {rejectMode ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            style={{ flex: 1, padding: '8px 10px', fontSize: 12, color: '#FFF',
                     background: 'rgba(255,255,255,0.04)',
                     border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4 }}
            placeholder="Reason (optional)"
            value={reason} onChange={e => setReason(e.target.value)} autoFocus
          />
          <button disabled={busy}
            onClick={() => { onReject(item.id, reason.trim() || null); setRejectMode(false) }}
            style={{ ...btnPrimary, background: RED, color: '#FFF' }}>Confirm reject</button>
          <button disabled={busy} onClick={() => setRejectMode(false)} style={btnGhost}>Cancel</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button disabled={busy} onClick={() => setRejectMode(true)} style={btnGhost}>Reject</button>
          <button disabled={busy} onClick={() => onApprove(item.id)} style={btnPrimary}>
            {busy ? 'Working…' : 'Approve & apply'}
          </button>
        </div>
      )}
    </div>
  )
}


// Default-key resolvers — Pipedrive rows arrive with `deal_category` (string)
// and `status` ('open'/'won'/'lost') but no type_key/status_key. Compute the
// same defaults NewDealForm uses so the reviewer can Approve without touching
// the dropdowns.
function defaultTypeKeyForCategory(cat, dealTypes) {
  if (!cat) return null
  const rows = (dealTypes || []).filter(dt => dt.deal_category === cat)
  if (!rows.length) return null
  const plain = rows.find(dt => !dt.property_type && !dt.deal_type_detail)
  return plain ? plain.type_key : rows.slice().sort((a, b) => a.type_key - b.type_key)[0].type_key
}
function defaultStatusKeyForStatus(status, statuses) {
  if (!status) return null
  const norm = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()   // 'open' → 'Open'
  const rows = (statuses || []).filter(s => s.status === norm)
  if (!rows.length) return null
  const sorted = rows.slice().sort((a, b) => (a.stage_group || '').localeCompare(b.stage_group || ''))
  return norm === 'Open' ? sorted[0].status_key : sorted[sorted.length - 1].status_key
}

const selectStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: '4px 6px', fontSize: 12,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 4, color: '#FFFFFF', outline: 'none',
  colorScheme: 'dark',
}
const optStyle = { background: '#061020', color: '#FFFFFF' }

function ItemCard({ item, dealTypes, statuses, owners, orgs, onApprove, onReject, busy }) {
  const [rejectMode, setRejectMode] = useState(false)
  const [reason, setReason] = useState('')

  const f = item.extracted_fields || {}

  // Editable state — pre-fill from payload OR from defaults derived from
  // deal_category / status strings when the payload doesn't have integer keys.
  // Every DB-mapped field is editable. Empty on Approve → null (implicit mute).
  const [edits, setEdits] = useState(() => ({
    // Classification
    type_key:            f.type_key   ?? defaultTypeKeyForCategory(f.deal_category, dealTypes) ?? '',
    status_key:          f.status_key ?? defaultStatusKeyForStatus(f.status, statuses) ?? '',
    owner_key:           f.owner_key  ?? '',
    // Core
    name:                f.name ?? '',
    org:                 f.org ?? '',
    value:               f.value ?? '',
    priority:            f.priority ?? '',
    year_built:          f.year_built ?? '',
    // Location
    address:             f.address ?? '',
    city:                f.city ?? '',
    state:               f.state ?? '',
    zip:                 f.zip ?? '',
    // Property metrics
    nrsf:                f.nrsf ?? '',
    total_units:         f.total_units ?? '',
    price_per_foot:      f.price_per_foot ?? '',
    weighted_avg_rent:   f.weighted_avg_rent ?? '',
    street_rent:         f.street_rent ?? '',
    web_rent:            f.web_rent ?? '',
    // Lending metrics
    appraised_value:     f.appraised_value ?? '',
    uw_sizing:           f.uw_sizing ?? '',
    analyst_sizing:      f.analyst_sizing ?? '',
    loan_type:           f.loan_type ?? '',
    loan_pledge:         f.loan_pledge ?? '',
    recourse:            f.recourse ?? '',
    benchmark:           f.benchmark ?? '',
    term:                f.term ?? '',
    points_in:           f.points_in ?? '',
    points_out:          f.points_out ?? '',
    interest_rate:       f.interest_rate ?? '',
    ltv:                 f.ltv ?? '',
    ltc:                 f.ltc ?? '',
    debt_yield:          f.debt_yield ?? '',
    // SharePoint links
    link_main_folder:    f.link_main_folder ?? '',
    link_om:             f.link_om ?? '',
    link_uw_model:       f.link_uw_model ?? '',
    link_due_diligence:  f.link_due_diligence ?? '',
    link_financials:     f.link_financials ?? '',
    link_reit_reports:   f.link_reit_reports ?? '',
    link_sources_uses:   f.link_sources_uses ?? '',
    link_third_party_uw: f.link_third_party_uw ?? '',
    notes:               f.notes ?? '',
  }))

  // The currently-selected deal_category (via type_key lookup) tells us which
  // metric section to render — lending vs property. Reviewer can flip type_key
  // and the relevant section swaps in.
  const currentCategory = dealTypes.find(dt => dt.type_key === Number(edits.type_key))?.deal_category
  const isLending = currentCategory === 'lending'

  const typeLabel   = dealTypes.find(dt => dt.type_key   === f.type_key  )?.type_label   || `type ${f.type_key}`
  const statusLabel = statuses.find(s   => s.status_key  === f.status_key)?.stage        || `status ${f.status_key}`
  const ownerLabel  = owners.find(o     => o.owner_key   === f.owner_key )?.owner        || `owner ${f.owner_key}`

  // Field classification for override serialization.
  // Numeric fields become null when the input is empty. Text fields trim
  // whitespace and become null on empty. Key fields (type/status/owner) are
  // coerced to Number.
  const NUM_FIELDS = [
    'value', 'nrsf', 'total_units', 'price_per_foot',
    'weighted_avg_rent', 'street_rent', 'web_rent',
    'appraised_value', 'uw_sizing', 'analyst_sizing',
  ]
  const TEXT_FIELDS = [
    // Name/address/city/state/zip are display-only in the queue — the payload
    // carries them straight to the approve handler unmodified.
    'org',
    'priority', 'year_built',
    'loan_type', 'loan_pledge', 'recourse', 'benchmark',
    'term', 'points_in', 'points_out', 'interest_rate',
    'ltv', 'ltc', 'debt_yield',
    'link_main_folder', 'link_om', 'link_uw_model', 'link_due_diligence',
    'link_financials', 'link_reit_reports', 'link_sources_uses', 'link_third_party_uw',
    'notes',
  ]

  function overridesForApprove() {
    const out = {
      type_key:   edits.type_key   ? Number(edits.type_key)   : null,
      status_key: edits.status_key ? Number(edits.status_key) : null,
      owner_key:  edits.owner_key  ? Number(edits.owner_key)  : null,
    }
    for (const k of NUM_FIELDS) {
      const v = edits[k]
      out[k] = (v === '' || v == null) ? null : Number(v)
    }
    for (const k of TEXT_FIELDS) {
      const v = (edits[k] ?? '').toString().trim()
      out[k] = v || null
    }
    return out
  }

  // Small helpers to keep the JSX below readable.
  const setField = (k, v) => setEdits(x => ({ ...x, [k]: v }))
  const TextInput = ({ field, placeholder, span = 1 }) => (
    <div style={{ gridColumn: `span ${span}` }}>
      <span style={{ color: MUTED, fontSize: 10 }}>{field.replace(/_/g, ' ').toUpperCase()}</span>
      <input type="text" style={selectStyle} placeholder={placeholder || ''}
             value={edits[field] ?? ''}
             onChange={e => setField(field, e.target.value)} />
    </div>
  )
  const NumberInput = ({ field, placeholder, span = 1 }) => (
    <div style={{ gridColumn: `span ${span}` }}>
      <span style={{ color: MUTED, fontSize: 10 }}>{field.replace(/_/g, ' ').toUpperCase()}</span>
      <input type="number" style={selectStyle} placeholder={placeholder || ''}
             value={edits[field] ?? ''}
             onChange={e => setField(field, e.target.value)} />
    </div>
  )
  const sectionTitle = { color: '#94A3B8', fontSize: 9, letterSpacing: '0.15em',
                         fontWeight: 600, textTransform: 'uppercase', marginTop: 4 }

  return (
    <div style={cardStyle}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <div style={{ color: '#FFFFFF', fontSize: 15, fontWeight: 600 }}>{f.name || '(no name)'}</div>
        <div style={{ ...badgeStyle, background: 'rgba(212,175,55,0.15)', color: GOLD }}>
          {typeLabel}
        </div>
        <div style={{ ...badgeStyle, background: 'rgba(34,197,94,0.15)', color: GREEN }}>NEW DEAL</div>
        {f.kind === 'pipedrive_sync' && (
          <a href={f.pipedrive_url} target="_blank" rel="noreferrer"
             title={`Open in Pipedrive (${f.pipedrive_instance})`}
             style={{
               ...badgeStyle,
               background: 'rgba(139,92,246,0.15)', color: '#B794F4',
               textDecoration: 'none',
             }}>
            PD · {f.pipedrive_instance}
          </a>
        )}
        <div style={{ color: MUTED, fontSize: 11 }}>
          from {item.sender_email || '?'} · {new Date(item.received_at).toLocaleString()}
        </div>
      </div>

      {/* ── CLASSIFICATION ─────────────────────────────────────────────── */}
      <div style={sectionTitle}>Classification</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, fontSize: 12 }}>
        <div>
          <span style={{ color: MUTED, fontSize: 10 }}>DEAL TYPE</span>
          <select style={selectStyle} value={edits.type_key || ''}
                  onChange={e => setField('type_key', e.target.value)}>
            <option value="" style={optStyle}>— pick —</option>
            {Object.values(
              dealTypes.reduce((acc, dt) => {
                const cat = dt.deal_category
                if (!cat) return acc
                if (!acc[cat] || dt.type_key < acc[cat].type_key) acc[cat] = dt
                return acc
              }, {})
            ).map(dt => (
              <option key={dt.type_key} value={dt.type_key} style={optStyle}>
                {dt.deal_category.charAt(0).toUpperCase() + dt.deal_category.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <span style={{ color: MUTED, fontSize: 10 }}>STATUS / STAGE</span>
          <select style={selectStyle} value={edits.status_key || ''}
                  onChange={e => setField('status_key', e.target.value)}>
            <option value="" style={optStyle}>— pick —</option>
            {statuses.map(s => (
              <option key={s.status_key} value={s.status_key} style={optStyle}>
                {s.status} · {s.stage}
              </option>
            ))}
          </select>
        </div>
        <div>
          <span style={{ color: MUTED, fontSize: 10 }}>OWNER</span>
          <select style={selectStyle} value={edits.owner_key || ''}
                  onChange={e => setField('owner_key', e.target.value)}>
            <option value="" style={optStyle}>— pick —</option>
            {owners.map(o => (
              <option key={o.owner_key} value={o.owner_key} style={optStyle}>{o.owner}</option>
            ))}
          </select>
        </div>
        <NumberInput field="value" placeholder="$" />
      </div>

      {/* ── CORE / LOCATION ────────────────────────────────────────────── */}
      <div style={sectionTitle}>Core & Location</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, fontSize: 12 }}>
        {/* Name — read-only; the deal title from Pipedrive is canonical */}
        <div style={{ gridColumn: 'span 2' }}>
          <span style={{ color: MUTED, fontSize: 10 }}>NAME</span><br/>
          <span style={{ color: '#FFFFFF' }}>{fmtVal(f.name)}</span>
        </div>
        {/* Org — editable + datalist autocomplete backed by dim_org */}
        <div style={{ gridColumn: 'span 2' }}>
          <span style={{ color: MUTED, fontSize: 10 }}>ORG</span>
          <input type="text" list={`orgs-${item.id}`} style={selectStyle}
                 placeholder="Broker / seller (pick or type new)"
                 value={edits.org}
                 onChange={e => setField('org', e.target.value)} />
          <datalist id={`orgs-${item.id}`}>
            {orgs.map(o => (
              <option key={o.org_key} value={o.org_name} />
            ))}
          </datalist>
        </div>
        {/* Address / City / State / ZIP — read-only; edit via New Deal form
            or a future "Propose Change" flow if needed */}
        <div style={{ gridColumn: 'span 4' }}>
          <span style={{ color: MUTED, fontSize: 10 }}>ADDRESS</span><br/>
          <span style={{ color: '#FFFFFF' }}>{fmtVal(f.address)}</span>
          {f.city  && <span style={{ color: MUTED }}> · {f.city}</span>}
          {f.state && <span style={{ color: MUTED }}> · {f.state}</span>}
          {f.zip   && <span style={{ color: MUTED }}> · {f.zip}</span>}
          {f.lat && f.lng && <span style={{ color: MUTED, fontSize: 10 }}> · {f.lat.toFixed(4)},{f.lng.toFixed(4)}</span>}
        </div>
      </div>

      {/* ── PROPERTY METRICS ───────────────────────────────────────────── */}
      <div style={sectionTitle}>Property metrics</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, fontSize: 12 }}>
        <NumberInput field="nrsf" />
        <NumberInput field="total_units" />
        <NumberInput field="price_per_foot" />
        <TextInput   field="year_built" />
        {!isLending && (
          <>
            <NumberInput field="weighted_avg_rent" />
            <NumberInput field="street_rent" />
            <NumberInput field="web_rent" />
            <TextInput   field="priority" placeholder="High / Medium / Low" />
          </>
        )}
      </div>

      {/* ── LENDING METRICS (only when type_key resolves to Lending) ───── */}
      {isLending && (
        <>
          <div style={sectionTitle}>Lending metrics</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, fontSize: 12 }}>
            <NumberInput field="appraised_value" />
            <NumberInput field="uw_sizing" />
            <NumberInput field="analyst_sizing" />
            {/* Enum-backed dropdowns (labels resolved server-side from Pipedrive) */}
            <div>
              <span style={{ color: MUTED, fontSize: 10 }}>LOAN TYPE</span>
              <select style={selectStyle} value={edits.loan_type || ''}
                      onChange={e => setField('loan_type', e.target.value)}>
                <option value="" style={optStyle}>—</option>
                <option value="Construction" style={optStyle}>Construction</option>
                <option value="Bridge"       style={optStyle}>Bridge</option>
              </select>
            </div>
            <div>
              <span style={{ color: MUTED, fontSize: 10 }}>LOAN PLEDGE</span>
              <select style={selectStyle} value={edits.loan_pledge || ''}
                      onChange={e => setField('loan_pledge', e.target.value)}>
                <option value=""          style={optStyle}>—</option>
                <option value="Axos"      style={optStyle}>Axos</option>
                <option value="BV"        style={optStyle}>BV</option>
                <option value="Churchill" style={optStyle}>Churchill</option>
                <option value="CRB"       style={optStyle}>CRB</option>
                <option value="JPM"       style={optStyle}>JPM</option>
              </select>
            </div>
            <div>
              <span style={{ color: MUTED, fontSize: 10 }}>RECOURSE</span>
              <select style={selectStyle} value={edits.recourse || ''}
                      onChange={e => setField('recourse', e.target.value)}>
                <option value=""                  style={optStyle}>—</option>
                <option value="Full Recourse"     style={optStyle}>Full Recourse</option>
                <option value="Partial Recourse"  style={optStyle}>Partial Recourse</option>
                <option value="None Recourse"     style={optStyle}>None Recourse</option>
              </select>
            </div>
            <div>
              <span style={{ color: MUTED, fontSize: 10 }}>BENCHMARK</span>
              <select style={selectStyle} value={edits.benchmark || ''}
                      onChange={e => setField('benchmark', e.target.value)}>
                <option value=""       style={optStyle}>—</option>
                <option value="Sofr"   style={optStyle}>Sofr</option>
                <option value="Prime"  style={optStyle}>Prime</option>
              </select>
            </div>
            <TextInput   field="term" />
            <TextInput   field="points_in" />
            <TextInput   field="points_out" />
            <TextInput   field="interest_rate" />
            <TextInput   field="ltv" />
            <TextInput   field="ltc" />
            <TextInput   field="debt_yield" />
          </div>
        </>
      )}

      {/* ── SHAREPOINT LINKS ───────────────────────────────────────────── */}
      <div style={sectionTitle}>SharePoint links</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, fontSize: 12 }}>
        <TextInput field="link_main_folder"    span={2} placeholder="https:// — Main folder" />
        <TextInput field="link_om"                     placeholder="https:// — OM" />
        <TextInput field="link_uw_model"               placeholder="https:// — UW model" />
        <TextInput field="link_due_diligence"          placeholder="https:// — Due diligence" />
        <TextInput field="link_financials"             placeholder="https:// — Financials" />
        <TextInput field="link_reit_reports"           placeholder="https:// — REIT reports" />
        <TextInput field="link_sources_uses"           placeholder="https:// — Sources & uses" />
        <TextInput field="link_third_party_uw" span={2} placeholder="https:// — Third-party UW" />
      </div>

      {/* ── NOTES ──────────────────────────────────────────────────────── */}
      <div style={sectionTitle}>Notes</div>
      <textarea rows={2} style={{ ...selectStyle, resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="Reviewer notes (optional)"
                value={edits.notes}
                onChange={e => setField('notes', e.target.value)} />

      {/* Actions */}
      {rejectMode ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            style={{ flex: 1, padding: '8px 10px', fontSize: 12, color: '#FFF',
                     background: 'rgba(255,255,255,0.04)',
                     border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4 }}
            placeholder="Reason for rejection (optional)"
            value={reason} onChange={e => setReason(e.target.value)}
            autoFocus
          />
          <button
            disabled={busy}
            onClick={() => { onReject(item.id, reason.trim() || null); setRejectMode(false) }}
            style={{ ...btnPrimary, background: RED, color: '#FFF' }}>
            Confirm reject
          </button>
          <button disabled={busy} onClick={() => setRejectMode(false)} style={btnGhost}>Cancel</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button disabled={busy} onClick={() => setRejectMode(true)} style={btnGhost}>Reject</button>
          <button disabled={busy} onClick={() => onApprove(item.id, overridesForApprove())} style={btnPrimary}>
            {busy ? 'Working…' : 'Approve & promote'}
          </button>
        </div>
      )}
    </div>
  )
}


export default function ReviewQueue() {
  const queryClient = useQueryClient()
  const { data: dims } = useDims()
  const { data: deals = [] } = useDeals()
  const { data: pending, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['intake', 'pending_review'],
    queryFn:  () => listIntake('pending_review'),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })

  const [busyId, setBusyId] = useState(null)
  const [banner, setBanner] = useState(null)

  const items = pending?.items || []

  const approveMut = useMutation({
    mutationFn: ({ id, overrides }) => approveIntake(id, overrides),
    onSuccess: (data) => {
      setBanner({ type: 'ok', text: `Approved → ${data.deal.id} (${data.deal.name})` })
      queryClient.invalidateQueries({ queryKey: ['intake'] })
      queryClient.invalidateQueries({ queryKey: ['deals'] })
    },
    onError: (err) => setBanner({ type: 'err', text: `Approve failed: ${err.message}` }),
    onSettled: () => setBusyId(null),
  })

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }) => rejectIntake(id, reason),
    onSuccess: () => {
      setBanner({ type: 'ok', text: `Rejected.` })
      queryClient.invalidateQueries({ queryKey: ['intake'] })
    },
    onError: (err) => setBanner({ type: 'err', text: `Reject failed: ${err.message}` }),
    onSettled: () => setBusyId(null),
  })

  function handleApprove(id, overrides = {}) { setBusyId(id); approveMut.mutate({ id, overrides }) }
  function handleReject(id, reason) { setBusyId(id); rejectMut.mutate({ id, reason }) }

  return (
    <div style={{ height: '100%', overflow: 'auto', background: NAVY, padding: '24px 28px' }}>
      <div style={{ maxWidth: 960, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <h1 style={{ color: '#FFFFFF', fontSize: 20, margin: 0, fontWeight: 600 }}>Review Queue</h1>
          <span style={{ color: MUTED, fontSize: 12 }}>
            {items.length} pending · admin approval promotes to the map
          </span>
          <button onClick={() => refetch()} style={{ ...btnGhost, marginLeft: 'auto' }}>Refresh</button>
        </div>

        {banner && (
          <div style={{
            padding: '10px 14px', borderRadius: 6, fontSize: 12,
            background: banner.type === 'ok' ? 'rgba(34,197,94,0.12)' : 'rgba(231,76,60,0.12)',
            border: `1px solid ${banner.type === 'ok' ? GREEN : RED}`,
            color: banner.type === 'ok' ? GREEN : RED,
          }}>{banner.text}</div>
        )}

        {isLoading && <div style={{ color: MUTED }}>Loading queue…</div>}
        {isError && <div style={{ color: RED }}>{error.message}</div>}
        {!isLoading && items.length === 0 && (
          <div style={{ ...cardStyle, alignItems: 'center', color: MUTED, padding: 40 }}>
            Nothing to review. New submissions land here for admin approval before showing on the map.
          </div>
        )}

        {/* Wait for dims before mounting ItemCards — otherwise the useState
            initializer runs with empty dealTypes, defaultTypeKeyForCategory
            returns null, and the Lending-metrics section never appears. */}
        {!dims && items.length > 0 && <div style={{ color: MUTED }}>Loading dropdown data…</div>}

        {dims && items.map(item => {
          // Legacy double-encoded rows land here as JSON strings (bug fixed in
          // routes/deals.py but old rows persist). Parse defensively.
          let fields = item.extracted_fields
          if (typeof fields === 'string') {
            try { fields = JSON.parse(fields) } catch { fields = {} }
            item = { ...item, extracted_fields: fields }
          }
          const kind = fields?.kind || 'new_deal'
          if (kind === 'link_update') {
            return (
              <LinkUpdateCard
                key={item.id}
                item={item}
                deals={deals}
                busy={busyId === item.id}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            )
          }
          return (
            <ItemCard
              key={item.id}
              item={item}
              dealTypes={dims?.deal_types || []}
              statuses={dims?.statuses || []}
              owners={dims?.owners || []}
              orgs={dims?.orgs || []}
              busy={busyId === item.id}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          )
        })}

      </div>
    </div>
  )
}
